import { AxiosResponse } from 'axios';
import {
  SET_KABBALAHS_N,
  SET_KABBALAH_N,
  SET_KABBALAH_N_STATS,
  SET_ERRORMSG,
} from '@/store/mutation_types';
import {
  APPROVE_KABBALAH_N,
  CLEAR_KABBALAH_N_ISLOADING,
  DELETE_KABBALAH_N,
  EDIT_KABBALAH_DISPLAY,
  EDIT_KABBALAH_N,
  GET_KABBALAH_N,
  GET_KABBALAH_N_STATS,
  GET_KABBALAHS_N,
  MARK_VIEWED_KABBALAH_N,
  REGISTER_KABBALAH_N,
  REJECT_KABBALAH_N,
  TOGGLE_KABBALAH_N_IS_ACTIVE,
} from '@/store/action_types';
import { StateObject } from '@/dtos/state.dto';
import Kabbalah_N from '@/services/kabbalah_N';
import { Module } from 'vuex';
import {
  EditKabbalah_N,
  GetKabbalah_N,
  GetKabbalahStats_N,
  RegisterKabbalah_N,
  SaveKabbalahDisplayDestination_N,
} from '@/dtos/kabbalah_N.dto';

const kabbalah_N: Module<any, {}> = {
  state: {
    kabbalah_N: {
      data: null,
      isLoading: false,
    },
    kabbalahs_N: {
      data: null,
      isLoading: false,
    },
    // Server-side counts per status, kept beside the list rather than derived
    // from it — the list is paged in the UI, the counts describe the whole
    // queue.
    kabbalahStats_N: {
      data: null,
      isLoading: false,
    },
  },
  getters: {
    kabbalah_N: (state: { kabbalah_N: any }) => state.kabbalah_N,
    kabbalahs_N: (state: { kabbalahs_N: any }) => state.kabbalahs_N,
    kabbalahStats_N: (state: { kabbalahStats_N: any }) => state.kabbalahStats_N,
  },
  mutations: {
    [SET_KABBALAH_N]: (
      state: { kabbalah_N: StateObject },
      payload: { data: any; isLoading: any }
    ) => {
      if (payload.data != null) state.kabbalah_N.data = payload.data;
      state.kabbalah_N.isLoading = payload.isLoading;
    },
    [SET_KABBALAHS_N]: (
      state: { kabbalahs_N: StateObject },
      payload: { data: any; isLoading: any }
    ) => {
      if (payload.data != null) state.kabbalahs_N.data = payload.data;
      state.kabbalahs_N.isLoading = payload.isLoading;
    },
    [SET_KABBALAH_N_STATS]: (
      state: { kabbalahStats_N: StateObject },
      payload: { data: any; isLoading: any }
    ) => {
      if (payload.data != null) state.kabbalahStats_N.data = payload.data;
      state.kabbalahStats_N.isLoading = payload.isLoading;
    },
  },
  actions: {
    [REGISTER_KABBALAH_N]: async ({ commit }: any, payload: RegisterKabbalah_N) => {
      commit(SET_KABBALAH_N, { data: null, isLoading: true });

      const result: AxiosResponse = await Kabbalah_N.registerKabbalah_N(payload);

      if (result) {
        if (result.data.status) {
          commit(SET_KABBALAH_N, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_KABBALAH_N, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      commit(SET_KABBALAH_N, { isLoading: false });
      return false;
    },
    [GET_KABBALAHS_N]: async ({ commit }: any, payload: any) => {
      commit(SET_KABBALAHS_N, { data: null, isLoading: true });

      const result: AxiosResponse = await Kabbalah_N.getKabbalahs_N(
        payload as GetKabbalah_N
      );

      if (result) {
        if (result.data.status) {
          commit(SET_KABBALAHS_N, { data: result.data.data, isLoading: false });
          return result.data.data;
        }
        commit(SET_KABBALAHS_N, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      commit(SET_KABBALAHS_N, { isLoading: false });
      return false;
    },
    /**
     * Counts for the submission tabs. The commit deliberately carries no data
     * while the request is open, so the bubbles hold their last numbers instead
     * of blanking out on every refresh.
     */
    [GET_KABBALAH_N_STATS]: async ({ commit }: any, payload: any) => {
      commit(SET_KABBALAH_N_STATS, { data: null, isLoading: true });

      const result: AxiosResponse = await Kabbalah_N.getKabbalahStats_N(
        payload as GetKabbalahStats_N
      );

      if (result) {
        if (result.data.status) {
          commit(SET_KABBALAH_N_STATS, {
            data: result.data.data,
            isLoading: false,
          });
          return result.data.data;
        }
        commit(SET_KABBALAH_N_STATS, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      commit(SET_KABBALAH_N_STATS, { isLoading: false });
      return false;
    },
    [GET_KABBALAH_N]: async ({ commit }: any, payload: any) => {
      commit(SET_KABBALAH_N, { data: null, isLoading: true });

      const result: AxiosResponse = await Kabbalah_N.getKabbalahs_N(
        payload as GetKabbalah_N
      );

      if (result) {
        if (result.data.status) {
          commit(SET_KABBALAH_N, {
            data: result.data.data?.[0] ?? null,
            isLoading: false,
          });
          return result.data.data?.[0];
        }
        commit(SET_KABBALAH_N, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      commit(SET_KABBALAH_N, { isLoading: false });
      return false;
    },
    [EDIT_KABBALAH_N]: async ({ commit, state }: any, payload: EditKabbalah_N) => {
      commit(SET_KABBALAH_N, { data: null, isLoading: true });

      const result: AxiosResponse = await Kabbalah_N.editKabbalah_N(payload);

      if (result) {
        if (result.data.status) {
          // Keep the loaded list in step with the saved record so an inline edit
          // doesn't need a full refetch.
          if (Array.isArray(state.kabbalahs_N?.data)) {
            const index = state.kabbalahs_N.data.findIndex(
              (item: any) => item.id === result.data.data.id
            );
            if (index !== -1) {
              state.kabbalahs_N.data[index] = {
                ...state.kabbalahs_N.data[index],
                ...result.data.data,
              };
            }
          }
          commit(SET_KABBALAH_N, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_KABBALAH_N, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      commit(SET_KABBALAH_N, { isLoading: false });
      return false;
    },
    [TOGGLE_KABBALAH_N_IS_ACTIVE]: async ({ commit, state }: any, payload: { id: string }) => {
      commit(SET_KABBALAH_N, { data: { id: payload.id }, isLoading: true });

      const result: AxiosResponse = await Kabbalah_N.toggleKabbalah_NIsActive(payload);

      if (result) {
        if (result.data.status) {
          if (Array.isArray(state.kabbalahs_N?.data)) {
            const index = state.kabbalahs_N.data.findIndex(
              (item: any) => item.id === result.data.data.id
            );
            if (index !== -1) {
              state.kabbalahs_N.data[index].isActive = result.data.data.isActive;
            }
          }
          commit(SET_KABBALAH_N, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_KABBALAH_N, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      commit(SET_KABBALAH_N, { isLoading: false });
      return false;
    },
    [APPROVE_KABBALAH_N]: async ({ commit, state }: any, payload: string) => {
      commit(SET_KABBALAH_N, { data: null, isLoading: true });

      const result: AxiosResponse = await Kabbalah_N.approveKabbalah_N(payload);

      if (result) {
        if (result.data.status) {
          if (Array.isArray(state.kabbalahs_N?.data)) {
            const index = state.kabbalahs_N.data.findIndex(
              (item: any) => item.id === result.data.data.id
            );
            if (index !== -1) {
              state.kabbalahs_N.data[index].status = result.data.data.status;
              state.kabbalahs_N.data[index].approvedDate = result.data.data.approvedDate;
              state.kabbalahs_N.data[index].rejectedDate = result.data.data.rejectedDate;
            }
          }
          commit(SET_KABBALAH_N, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_KABBALAH_N, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      commit(SET_KABBALAH_N, { isLoading: false });
      return false;
    },
    [REJECT_KABBALAH_N]: async ({ commit, state }: any, payload: string) => {
      commit(SET_KABBALAH_N, { data: null, isLoading: true });

      const result: AxiosResponse = await Kabbalah_N.rejectKabbalah_N(payload);

      if (result) {
        if (result.data.status) {
          if (Array.isArray(state.kabbalahs_N?.data)) {
            const index = state.kabbalahs_N.data.findIndex(
              (item: any) => item.id === result.data.data.id
            );
            if (index !== -1) {
              state.kabbalahs_N.data[index].status = result.data.data.status;
              state.kabbalahs_N.data[index].approvedDate = result.data.data.approvedDate;
              state.kabbalahs_N.data[index].rejectedDate = result.data.data.rejectedDate;
            }
          }
          commit(SET_KABBALAH_N, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_KABBALAH_N, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      commit(SET_KABBALAH_N, { isLoading: false });
      return false;
    },
    /**
     * Marks a submission as read. Unlike its neighbours this touches neither
     * `isLoading` flag: it fires in the background every time a row is opened,
     * and `kabbalah_N.isLoading` is what the Add / Edit / Delete modals spin on
     * — a row click has no business moving those. Only the matching list entry
     * is patched, so the NEW badge flips without a refetch.
     */
    [MARK_VIEWED_KABBALAH_N]: async ({ state }: any, payload: string) => {
      const result: AxiosResponse = await Kabbalah_N.markViewedKabbalah_N(payload);

      if (result?.data?.status) {
        if (Array.isArray(state.kabbalahs_N?.data)) {
          const index = state.kabbalahs_N.data.findIndex(
            (item: any) => item.id === result.data.data.id
          );
          if (index !== -1) {
            state.kabbalahs_N.data[index].isViewed = result.data.data.isViewed;
          }
        }
        return true;
      }
      return false;
    },
    [DELETE_KABBALAH_N]: async ({ commit }: any, payload: string) => {
      commit(SET_KABBALAH_N, { data: null, isLoading: true });

      const result: AxiosResponse = await Kabbalah_N.deleteKabbalah_N(payload);

      if (result) {
        if (result.data.status) {
          commit(SET_KABBALAH_N, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_KABBALAH_N, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      commit(SET_KABBALAH_N, { isLoading: false });
      return false;
    },
    /**
     * The organization-wide display destination. Lives on the organization, so
     * the saved row is handed back for the caller to fold into its own copy of
     * the account rather than being written into this module's state.
     */
    [EDIT_KABBALAH_DISPLAY]: async (
      { commit }: any,
      payload: SaveKabbalahDisplayDestination_N
    ) => {
      const result: AxiosResponse = await Kabbalah_N.saveKabbalahDisplayDestination(payload);

      if (result) {
        if (result.data.status) {
          return result.data.data;
        }
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      return false;
    },
    [CLEAR_KABBALAH_N_ISLOADING]: ({ commit }) => {
      commit(SET_KABBALAH_N, { isLoading: false });
      commit(SET_KABBALAHS_N, { isLoading: false });
      commit(SET_KABBALAH_N_STATS, { isLoading: false });
    },
  },
};

export default kabbalah_N;
